import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';
import { logger } from '@/lib/structured-logger';

type UserRole = Database['public']['Enums']['app_role'];

interface UseUserRoleReturn {
  role: UserRole | null;
  loading: boolean;
  isOwner: boolean;
  isAdmin: boolean;
  isEditor: boolean;
  isViewer: boolean;
  canManageDNA: boolean; // owner or admin
  canEditContent: boolean; // owner, admin, or editor
  refetch: () => Promise<void>;
}

/**
 * Hook to get the current user's role in their organization
 * Implements role-based access control helpers
 */
export const useUserRole = (): UseUserRoleReturn => {
  const { user, organization } = useAuth();
  const [role, setRole] = useState<UserRole | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchRole = async () => {
    if (!user?.id || !organization?.id) {
      setRole(null);
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id)
        .eq('organization_id', organization.id)
        .maybeSingle();

      if (error) {
        logger.error('Failed to fetch user role', { error, userId: user.id, organizationId: organization.id });
        setRole(null);
      } else {
        setRole(data?.role || null);
      }
    } catch (error) {
      logger.error('Error in fetchRole', { error });
      setRole(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRole();
  }, [user?.id, organization?.id]);

  return {
    role,
    loading,
    isOwner: role === 'owner',
    isAdmin: role === 'admin' || role === 'owner',
    isEditor: role === 'editor',
    isViewer: role === 'viewer',
    canManageDNA: role === 'owner' || role === 'admin',
    canEditContent: role === 'owner' || role === 'admin' || role === 'editor',
    refetch: fetchRole,
  };
};
